import React, { useEffect } from 'react';
import { SECTION_META_FIELDS, SECTION_REMARKS, CLEARANCE_REASONS } from './clearanceTemplate';
import './ClearancePrint.css';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '');

const SectionBlock = ({ section }) => {
    const metaFields = SECTION_META_FIELDS[section.key] || [];
    return (
        <div className="cp-section">
            <div className="cp-section-title">{section.title}</div>

            {(metaFields.length > 0 || section.date) && (
                <div className="cp-meta-row">
                    {metaFields.map(mf => (
                        <span key={mf.key} className="cp-meta">
                            <b>{mf.label}:</b> {mf.type === 'date'
                                ? fmtDate(section.meta?.[mf.key])
                                : (section.meta?.[mf.key] || '')}
                        </span>
                    ))}
                    <span className="cp-meta"><b>Date:</b> {fmtDate(section.date)}</span>
                </div>
            )}

            <table className="cp-table">
                <thead>
                    <tr>
                        <th className="cp-no">No.</th>
                        <th className="cp-part">Particulars</th>
                        <th className="cp-desc">Description</th>
                        <th className="cp-status">Status</th>
                        <th className="cp-remarks">Remarks</th>
                    </tr>
                </thead>
                <tbody>
                    {section.lineItems.map((li, i) => (
                        <tr key={i}>
                            <td className="cp-no">{li.particulars ? i + 1 : ''}</td>
                            <td className="cp-part">{li.particulars}</td>
                            <td className="cp-desc">{li.description}</td>
                            <td className="cp-status">{li.status}</td>
                            <td className="cp-remarks">{li.remarks}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {SECTION_REMARKS.includes(section.key) && (
                <div className="cp-remarks-box">
                    <b>Remarks / Comments:</b>
                    <div className="cp-remarks-text">{section.meta?.remarks || ''}</div>
                </div>
            )}

            {(section.documents || []).length > 0 && (
                <div className="cp-remarks-box">
                    <b>Attachments:</b>
                    <div className="cp-remarks-text">
                        {section.documents.map(d => d.fileName).join(', ')}
                    </div>
                </div>
            )}

            <div className="cp-signoff">
                {section.signedByName ? (
                    <>
                        <b>Digitally signed by:</b> {section.signedByName}
                        <span className="cp-signoff-date"> — {fmtDate(section.signedAt)}</span>
                        <div className="cp-signoff-note">
                            Electronically authorised through PrimeOffice Manager. No handwritten signature required.
                        </div>
                    </>
                ) : (
                    <span className="cp-signoff-pending"><b>Status:</b> Not yet completed</span>
                )}
            </div>
        </div>
    );
};

const ClearancePrint = ({ form, onDone }) => {
    useEffect(() => {
        const t = setTimeout(() => window.print(), 400);
        return () => clearTimeout(t);
    }, []);

    const emp = form.employee || {};
    const reasonValue = form.clearance?.reason;

    return (
        <div className="cp-screen">
            <div className="cp-toolbar no-print">
                <button className="btn btn-primary" onClick={() => window.print()}>🖨 Print / Save PDF</button>
                <button className="btn btn-secondary" onClick={onDone}>Close</button>
            </div>

            <div className="cp-page">
                <div className="cp-brand">BRADMA GROUP</div>
                <div className="cp-doctitle">CLEARANCE FORM</div>
                {form.clearanceId && <div className="cp-refno">Ref: {form.clearanceId}</div>}

                <div className="cp-block">
                    <div className="cp-block-title">Employee Details</div>
                    <div className="cp-emp-grid">
                        <span><b>Staff Name:</b> {emp.name || ''}</span>
                        <span><b>Staff Code:</b> {emp.staffCode || ''}</span>
                        <span><b>Staff Designation:</b> {emp.designation || ''}</span>
                        <span><b>Reporting To:</b> {emp.reportingTo || ''}</span>
                        <span><b>Branch:</b> {emp.branch || ''}</span>
                        <span><b>Date:</b> {fmtDate(emp.date)}</span>
                        <span><b>Staff QID:</b> {emp.qid || ''}</span>
                        <span><b>QID Expiry Date:</b> {fmtDate(emp.qidExpiry)}</span>
                    </div>
                </div>

                <div className="cp-block">
                    <div className="cp-block-title">Clearance Details</div>
                    <div className="cp-reason-row">
                        <b>Reason for Clearance Certificate:</b>
                        {CLEARANCE_REASONS.map(r => (
                            <span key={r.value} className={`cp-reason ${reasonValue === r.value ? 'checked' : ''}`}>
                                {reasonValue === r.value ? '☑' : '☐'} {r.label}
                            </span>
                        ))}
                        {reasonValue === 'other' && form.clearance?.reasonOther
                            ? <span className="cp-reason-other">— {form.clearance.reasonOther}</span> : null}
                    </div>
                    {form.clearance?.travelDate && (
                        <div className="cp-reason-row">
                            <b>Travel Date:</b> {fmtDate(form.clearance.travelDate)}
                        </div>
                    )}
                </div>

                {form.sections.map(section => (
                    <SectionBlock key={section.key} section={section} />
                ))}

                <div className="cp-footer">BRG/HR/LA/1.1</div>
            </div>
        </div>
    );
};

export default ClearancePrint;
